import React, { useState, useEffect, useRef } from 'react';
import Icon from '../shared/Icon';
import { createPortal } from 'react-dom';
import { useFlag } from '@unleash/proxy-client-react';
import './WidgetCreatorModal.css';
import { apiService } from '../../../services/api';
import { useWidgetQuery } from './useWidgetQuery';
import { getRenderer, isImplementedWidgetType, rendererInput } from './renderers';
import { generatedKpiLabel } from './renderers/kpiFormat';
import { WIDGET_TEXT_EDIT_FLAG } from './WidgetTextEditModal';
import SummaryRow from './SummaryRow';
import { rangeLabel } from './insightsRange';
import DatasetMultiSelect from '../shared/DatasetMultiSelect';

// Step 0: Widget type categories. Each card names its rollout family; a card is
// offered only when its family flag is on AND the type has a renderer (see
// isOffered below). Chart is the shipped family and is always offered. The other
// cards stay listed here so the family assignment is in one place, but nothing
// draws them until both conditions hold — there is no "coming soon" state.
const WIDGET_CATEGORIES = [
  {
    label: 'METRICS',
    items: [
      { id: 'summary_row', family: 'metrics', icon: 'grid_view', name: 'Summary row', desc: 'Key metrics in a row (datasets, automations, alerts, tasks).' },
      { id: 'kpi', family: 'metrics', icon: 'looks_one', name: 'Single KPI', desc: 'One number or value.' },
    ],
  },
  {
    label: 'DATA',
    items: [
      { id: 'table', family: 'table', icon: 'table_chart', name: 'Table', desc: 'Tabular data from a query.' },
      { id: 'chart', family: 'chart', icon: 'show_chart', name: 'Chart', desc: 'Bar, line, pie, or other chart.' },
    ],
  },
  {
    label: 'LISTS AND FEEDS',
    items: [
      { id: 'exceptions', family: 'lists_feeds', icon: 'warning', name: 'Exceptions', desc: 'Issues and exceptions from alerts.' },
      { id: 'key_automations', family: 'lists_feeds', icon: 'mediation', name: 'Key Automations', desc: 'Active automations with status.' },
      { id: 'recent_activity', family: 'lists_feeds', icon: 'history', name: 'Recent Activity', desc: 'Recent audit log entries.' },
    ],
  },
  {
    label: 'SHORTCUTS',
    items: [
      { id: 'shortcuts', family: 'shortcuts', icon: 'link', name: 'Shortcuts', desc: 'Saved queries and dataset links.' },
    ],
  },
];

// The shipped family: no flag of its own, always offered. Named once so the picker
// does not spell "chart is a family" three different ways.
const CHART_FAMILY = 'chart';

// Unleash flag per non-chart family — children of the parent `insights_widgets`
// flag. resapi enforces the same flags on query and save, so hiding a card here is
// a courtesy, not the control.
const FAMILY_FLAGS = {
  metrics: 'insights_widgets_metrics',
  table: 'insights_widgets_table',
  lists_feeds: 'insights_widgets_lists_feeds',
  shortcuts: 'insights_widgets_shortcuts',
};

// Step 1 (chart only): Chart sub-types with grouped categories
const CHART_SUBTYPES = [
  {
    label: 'Foundational Four',
    items: [
      { id: 'bar', icon: 'bar_chart', name: 'Bar chart' },
      { id: 'line', icon: 'show_chart', name: 'Line chart' },
      { id: 'scatter', icon: 'scatter_plot', name: 'Scatter plot' },
    ],
  },
  {
    label: 'Common Variations',
    items: [
      { id: 'stacked_bar', icon: 'stacked_bar_chart', name: 'Stacked bar chart' },
      { id: 'grouped_bar', icon: 'bar_chart', name: 'Grouped bar chart' },
      { id: 'area', icon: 'area_chart', name: 'Area chart' },
      { id: 'histogram', icon: 'equalizer', name: 'Histogram' },
    ],
  },
  {
    label: 'Specialist Charts',
    items: [
      { id: 'pie', icon: 'pie_chart', name: 'Pie chart' },
      { id: 'doughnut', icon: 'donut_large', name: 'Doughnut chart' },
    ],
  },
];

// All chart IDs for the preview switcher dropdown
const ALL_CHART_OPTIONS = [
  { group: 'Foundational Four', items: [
    { id: 'bar', name: 'Bar chart' },
    { id: 'line', name: 'Line chart' },
    { id: 'scatter', name: 'Scatter plot' },
  ]},
  { group: 'Common Variations', items: [
    { id: 'stacked_bar', name: 'Stacked bar chart' },
    { id: 'grouped_bar', name: 'Grouped bar chart' },
    { id: 'area', name: 'Area chart' },
    { id: 'histogram', name: 'Histogram' },
  ]},
  { group: 'Specialist Charts', items: [
    { id: 'pie', name: 'Pie chart' },
    { id: 'doughnut', name: 'Doughnut chart' },
  ]},
];

// The wizard is one flow for every promptable type; Chart alone has a sub-type
// step. Steps are named so the body and footer key on what a step IS rather than
// on its position, which shifts by one when the sub-type step is absent.
const FLOW_CHART = ['type', 'chart_type', 'configure', 'preview', 'name'];
const FLOW_PROMPTABLE = ['type', 'configure', 'preview', 'name'];
// A Summary row is configured as a strip of tiles rather than as one prompt, so it
// gets its own step in place of Configure. Everything else about the flow is the
// same, which is the point: it is still choose, configure, see it, name it.
const FLOW_SUMMARY = ['type', 'tiles', 'preview', 'name'];

const MIN_TILES = 2;
const MAX_TILES = 6;
// Mirrors widget_routes.PLATFORM_METRICS. A metric the backend cannot resolve would
// render as a permanently broken cell, so the two lists have to agree.
const TILE_METRICS = [
  { id: 'datasets', name: 'Datasets' },
  { id: 'automations', name: 'Active automations' },
  { id: 'alerts', name: 'Alerts held' },
  { id: 'review_tasks', name: 'CSV reviews pending' },
];
const TILE_RANGES = [
  { id: 'all', name: 'All time' },
  { id: '7d', name: 'Last 7 days' },
  { id: '30d', name: 'Last 30 days' },
  { id: '90d', name: 'Last 90 days' },
  { id: '12m', name: 'Last 12 months' },
];
// New tiles are platform counts by default. A prompted tile is an agent run, and the
// query gate lets three through at a time, so a row of six prompts serialises into
// two waves and queues every other card on the dashboard behind it. Platform tiles
// cost a cheap count and take no slot.
const newTile = (metric = 'datasets') => ({
  kind: 'platform',
  metric,
  label: TILE_METRICS.find((m) => m.id === metric)?.name || '',
});
const STEP_LABELS = {
  type: 'Choose type',
  chart_type: 'Chart type',
  tiles: 'Tiles',
  configure: 'Configure',
  preview: 'Preview',
  name: 'Name and add',
};
// Type ids that are a whole family in the picker but not a queryable type.
const isChartTypeId = (typeId) => ALL_CHART_OPTIONS.some((g) => g.items.some((it) => it.id === typeId));
const displayNameFor = (typeId) =>
  ALL_CHART_OPTIONS.flatMap((g) => g.items).find((i) => i.id === typeId)?.name
  || WIDGET_CATEGORIES.flatMap((c) => c.items).find((i) => i.id === typeId)?.name
  || typeId;

function WidgetCreatorModal({ open, onClose, onSave, editingWidget }) {
  const isEditMode = Boolean(editingWidget);
  // One hook call per family, unconditionally (hooks can't live in a loop). The
  // proxy client resolves an unknown flag to false, so a family is off until Unleash
  // says otherwise.
  const familyFlags = {
    [CHART_FAMILY]: true,
    metrics: useFlag(FAMILY_FLAGS.metrics),
    table: useFlag(FAMILY_FLAGS.table),
    lists_feeds: useFlag(FAMILY_FLAGS.lists_feeds),
    shortcuts: useFlag(FAMILY_FLAGS.shortcuts),
  };
  // PRD-1336 R7 (Option A): org-wide widget sharing.
  const widgetSharingEnabled = useFlag('org-widget-sharing');
  // PRD-1578: a KPI is named by a label rather than a title only with this on.
  const textEditEnabled = useFlag(WIDGET_TEXT_EDIT_FLAG);
  // A card is offered when its family is on and the type can actually be drawn.
  // The chart card is a family, not a renderer id, so it is offered outright.
  //
  // Editing also keeps the widget's OWN type on offer while its family flag is off,
  // which is exactly what resapi already permits: update_widget re-checks
  // availability only when the type CHANGES, so an existing widget stays editable
  // through a closed family. Without this the type step is a one-way door — the
  // current type is filtered out, nothing is highlighted, and the only card left to
  // click silently converts the widget to a chart on save.
  const editingTypeId = editingWidget?.widget_type_id || null;
  const isOffered = (item) =>
    item.family === CHART_FAMILY
    || (item.id === editingTypeId && isImplementedWidgetType(item.id))
    || (!!familyFlags[item.family] && isImplementedWidgetType(item.id));
  const visibleCategories = WIDGET_CATEGORIES
    .map((cat) => ({ ...cat, items: cat.items.filter(isOffered) }))
    .filter((cat) => cat.items.length > 0);
  // With nothing but Chart on offer, the "choose type" step has one answer.
  const onlyChartOffered = visibleCategories.every((cat) => cat.items.every((item) => item.family === CHART_FAMILY));

  const [step, setStep] = useState(0);
  const [selectedWidget, setSelectedWidget] = useState(null);  // widget category id
  const [selectedChart, setSelectedChart] = useState(null);     // chart sub-type id
  const [prompt, setPrompt] = useState('');
  const [datasets, setDatasets] = useState([]);
  // One or more datasets; ResAI decides from the prompt how to combine several.
  const [selectedDatasets, setSelectedDatasets] = useState([]);
  const [title, setTitle] = useState('');
  // PRD-1578: a KPI is named by its label, not a title. Pre-filled from the generated
  // unit + subtitle on reaching the last step, unless the author has already set one --
  // in this session, or saved on the widget being edited.
  const [label, setLabel] = useState('');
  const [labelTouched, setLabelTouched] = useState(false);
  // PRD-1336 R1: widget size is set on the canvas (drag-resize), not in the wizard.
  // The old width / widget_size controls + state were removed; the stored fields are
  // left in place but no longer written or read (canvas layout is authoritative).
  const [timeRange, setTimeRange] = useState('30d');
  // PRD-1336 R5: custom absolute range, encoded into timeRange as custom:<from>:<to>.
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  // PRD-1336 R3: author-set refresh policy.
  const [refreshPolicy, setRefreshPolicy] = useState('daily');
  const [refreshTime, setRefreshTime] = useState('08:00');
  const [refreshInterval, setRefreshInterval] = useState(60);
  // PRD-1336 R7: 'private' (creator-only) | 'organization' (whole org).
  const [visibility, setVisibility] = useState('private');
  const [tiles, setTiles] = useState([newTile('datasets'), newTile('automations')]);
  // The assembled strip, reported once by SummaryRow when every tile has settled.
  // It is both what the preview proves and what gets saved, so there is no second
  // path by which a strip could be stored without having been seen.
  const [summaryResult, setSummaryResult] = useState(null);
  const [examplePrompts, setExamplePrompts] = useState([]); // PRD-1336 R2: dataset-derived
  const previewRef = useRef(null);
  const [previewSize, setPreviewSize] = useState({ width: 0, height: 0 });

  const { status, data, error, errorType, metadata, executeQuery, reset } = useWidgetQuery();

  const isChart = selectedWidget === 'chart';
  const isSummary = selectedWidget === 'summary_row';
  const flow = isChart ? FLOW_CHART : isSummary ? FLOW_SUMMARY : FLOW_PROMPTABLE;
  const stepKey = flow[step];
  const steps = flow.map((k) => STEP_LABELS[k]);
  // What is actually sent to the backend and drawn: the chart sub-type for the
  // Chart family, the type itself for everything else.
  const queryTypeId = isChart ? selectedChart : selectedWidget;
  // What the Configure helper calls the thing being described.
  const promptNoun = isChart ? 'chart' : selectedWidget === 'table' ? 'table' : 'value';
  // "A KPI that takes a label": with the flag off a KPI keeps the title step as before.
  const isKpi = queryTypeId === 'kpi' && textEditEnabled;

  useEffect(() => {
    if (open) {
      apiService.getDatasets({ page: 1, page_size: 100 })
        .then((res) => setDatasets(res.items || []))
        .catch(() => setDatasets([]));
    }
  }, [open]);

  // Pre-populate fields when editing — once per open, or when a different widget is
  // handed over. The parent can pass a new object for the same widget on any
  // re-render; pre-filling on identity rewrote the step, datasets and sharing in the
  // middle of the author's edit.
  const prefilledIdRef = useRef(null);
  useEffect(() => {
    if (!open) {
      prefilledIdRef.current = null;
      return;
    }
    if (!editingWidget || prefilledIdRef.current === editingWidget.id) return;
    prefilledIdRef.current = editingWidget.id;
    const typeId = editingWidget.widget_type_id;
    const isChartType = isChartTypeId(typeId);
    setSelectedWidget(isChartType ? 'chart' : typeId);
    setSelectedChart(isChartType ? typeId : null);
    setPrompt(editingWidget.prompt || '');
    setSelectedDatasets(editingWidget.dataset_ids || []);
    setTitle(editingWidget.title || '');
    setLabel(editingWidget.display_label || '');
    // A saved label that is just the text the result generated is not the author's
    // wording, so an Edit prompt re-generates it from the new answer rather than
    // keeping a label that describes the old question.
    setLabelTouched(
      Boolean(editingWidget.display_label)
      && editingWidget.display_label !== generatedKpiLabel(editingWidget.data),
    );
    const savedRange = editingWidget.time_range || '30d';
    setTimeRange(savedRange);
    // A custom window is stored as custom:<from>:<to> but the two date inputs are
    // their own state, so without splitting it back out an edit renders them empty,
    // shows the "enter both dates" hint and blocks Next until the author retypes
    // dates they already chose.
    const [, from = '', to = ''] = savedRange.startsWith('custom:') ? savedRange.split(':') : [];
    setCustomFrom(from);
    setCustomTo(to);
    setRefreshPolicy(editingWidget.refresh_policy || 'daily');
    setRefreshTime(editingWidget.refresh_time || '08:00');
    setRefreshInterval(editingWidget.refresh_interval_minutes || 60);
    setVisibility(editingWidget.visibility || 'private');
    setTiles(Array.isArray(editingWidget.tiles) && editingWidget.tiles.length ? editingWidget.tiles : [newTile('datasets'), newTile('automations')]);
    setSummaryResult(null);
    // Open at the step AFTER the (already answered) type choice, named in whichever
    // flow this widget belongs to rather than by position.
    setStep(
      isChartType
        ? FLOW_CHART.indexOf('chart_type')
        : typeId === 'summary_row'
          ? FLOW_SUMMARY.indexOf('tiles')
          : FLOW_PROMPTABLE.indexOf('configure'),
    );
  }, [open, editingWidget]);


  // When Chart is the only type on offer, the first step has one answer — skip
  // it. Pre-select 'chart' and jump straight to the chart sub-type step the moment
  // the modal opens. Never for an edit: its type is already answered by the widget,
  // and this effect runs after the prepopulate effect with a stale `step`, so it
  // used to overwrite a KPI edit into the Chart flow whenever the family flag was
  // off — the state a deploy lands in — and the save then converted the widget.
  useEffect(() => {
    if (open && !editingWidget && onlyChartOffered && step === 0) {
      setSelectedWidget('chart');
      setStep(FLOW_CHART.indexOf('chart_type'));
    }
  }, [open, editingWidget, onlyChartOffered, step]);

  useEffect(() => {
    const measure = () => {
      if (previewRef.current) {
        const rect = previewRef.current.getBoundingClientRect();
        setPreviewSize({ width: rect.width, height: rect.height });
      }
    };
    measure();
    if (!previewRef.current) return;
    const observer = new ResizeObserver(measure);
    observer.observe(previewRef.current);
    return () => observer.disconnect();
  }, [step, status]);

  const handleClose = () => {
    setStep(0);
    setSelectedWidget(null);
    setSelectedChart(null);
    setPrompt('');
    setSelectedDatasets([]);
    setTitle('');
    setLabel('');
    setLabelTouched(false);
    setTimeRange('30d');
    setCustomFrom('');
    setCustomTo('');
    setRefreshPolicy('daily');
    setRefreshTime('08:00');
    setRefreshInterval(60);
    setExamplePrompts([]);
    setTiles([newTile('datasets'), newTile('automations')]);
    setSummaryResult(null);
    // Reset visibility too: the modal is mounted once and toggled via `open`, so state
    // persists across sessions. Without this, sharing one widget leaves the next NEW
    // widget's sharing toggle pre-checked — a private widget could ship org-wide by
    // default (review on #182).
    setVisibility('private');
    reset();
    onClose();
  };

  const runPreviewQuery = () => {
    executeQuery(queryTypeId, prompt, selectedDatasets, timeRange);
  };

  // Selection order, so the preview names datasets the way the author picked them.
  const selectedDatasetNames = selectedDatasets
    .map((id) => datasets.find((ds) => ds.id === id))
    .filter(Boolean)
    .map((ds) => ds.name || ds.id);

  const handleNext = () => {
    // Moving from Configure to Preview runs the query, whatever the type.
    if (stepKey === 'configure') runPreviewQuery();
    if (flow[step + 1] === 'name' && isKpi && !labelTouched) setLabel(generatedKpiLabel(data));
    setStep(step + 1);
  };

  const handleBack = () => {
    if (stepKey === 'preview') {
      reset();
      // The strip is re-run on the way forward; keeping the old one would let Add
      // stay enabled against tiles that are no longer the ones configured.
      setSummaryResult(null);
    }
    setStep(step - 1);
  };

  const handleSave = () => {
    if (onSave) {
      // In edit mode, keep the existing data when no fresh preview ran — but if the
      // prompt, dataset or range changed without a new preview, drop the stored bin
      // width, which was computed for the OLD config.
      //
      // This used to clear source_sql/source_tables here too. It no longer needs to:
      // those never leave the browser now (they are stripped on the way out, because a
      // request body carrying a literal SELECT is blocked by the gateway firewall), and
      // the server clears its own stored copy when it sees the config change. Deciding
      // that server-side is also more reliable — it compares against what is actually
      // in the row rather than against what this form believes was there.
      let fallbackData = isEditMode ? editingWidget.data : null;
      if (isEditMode && !data && fallbackData && typeof fallbackData === 'object') {
        const configChanged =
          prompt !== (editingWidget.prompt || '') ||
          selectedDatasets.join() !== (editingWidget.dataset_ids || []).join() ||
          timeRange !== (editingWidget.time_range || '30d');
        if (configChanged) {
          fallbackData = { ...fallbackData };
          delete fallbackData.bin_width;
        }
      }
      const payload = {
        id: isEditMode ? editingWidget.id : Date.now().toString(),
        widget_type_id: queryTypeId,
        // A KPI's title is never shown on its card, but it still names the widget in
        // the delete dialog, sharing and drill-down, so it follows the label -- ahead of
        // any title left in state from an earlier pass through another type's last step.
        title: (isKpi ? label.trim() || title : title) || prompt.slice(0, 40) || 'Untitled Widget',
        // Only the author's own wording is stored. An untouched field holds the generated
        // text, and saving that would freeze it as if written by hand, so it goes as ""
        // -- "never edited" -- and the card keeps following the result's own unit and
        // subtitle (review on #249). Undefined (non-KPI) leaves the column alone.
        displayLabel: isKpi ? (labelTouched ? label.trim() : '') : undefined,
        prompt,
        dataset_ids: selectedDatasets,
        // In edit mode, keep existing data unless a new query was run
        // A strip saves what the preview assembled; everything else saves the fresh
        // result, falling back to `fallbackData` -- which is main's edit-mode path and
        // carries the stale-bin-width handling, so it must not be replaced by the plain
        // `editingWidget.data` this branch used before the merge.
        data: (isSummary ? summaryResult : data) || fallbackData,
        // Tile CONFIGURATION, which is the widget's definition and lives in its own
        // column. Null for every other type, so a widget that is not a strip never
        // carries one.
        tiles: isSummary ? tiles : null,
        // A single-value payload carries empty axis labels; store null, not "".
        x_label: data?.x_label || (isEditMode ? editingWidget.x_label : null) || null,
        y_label: data?.y_label || (isEditMode ? editingWidget.y_label : null) || null,
        // PRD-1336 R1: width / widget_size are no longer written — the canvas
        // (react-grid-layout) owns the size; a new widget gets a chart-type default.
        // A strip has no widget-level range: each tile carries its own and a platform
        // tile has none. Storing one would put a range in the card's provenance
        // footer that describes nothing on the card.
        timeRange: isSummary ? null : timeRange,
        // PRD-1336 R3: refresh policy (daily carries a time; custom an interval).
        refreshPolicy,
        refreshTime: refreshPolicy === 'daily' ? refreshTime : null,
        refreshInterval: refreshPolicy === 'custom' ? Number(refreshInterval) : null,
        // PRD-1336 R7: only send an org-share when the feature is on; otherwise a
        // widget stays private regardless of any stale state.
        visibility: widgetSharingEnabled ? visibility : 'private',
        created_at: isEditMode ? editingWidget.created_at : new Date().toISOString(),
        isEdit: isEditMode,
      };
      onSave(payload);
    }
    handleClose();
  };

  // The preview has actually drawn something. The renderer needs a payload as well
  // as a success, so every gate that means "something rendered" reads this rather
  // than the status alone — a success carrying no payload would otherwise walk
  // through to Add and store a widget with `data: null`.
  // A Summary row has no single query: its strip reports itself once every tile has
  // settled, and it only reports at all when at least one tile rendered. So a row
  // whose fourth tile legitimately failed is still addable, while one that rendered
  // nothing is not — which is the same rule the other types get from `data`.
  const previewRendered = isSummary ? !!summaryResult : status === 'success' && !!data;

  const canNext = () => {
    if (stepKey === 'type') return !!selectedWidget;
    if (stepKey === 'chart_type') return !!selectedChart;
    if (stepKey === 'tiles') {
      if (tiles.length < MIN_TILES || tiles.length > MAX_TILES) return false;
      return tiles.every((t) => {
        // Every tile needs a label: it is the only thing naming the number, and the
        // card has one title for the whole strip.
        if (!String(t.label || '').trim()) return false;
        if (t.kind === 'platform') return TILE_METRICS.some((m) => m.id === t.metric);
        return !!String(t.prompt || '').trim() && !!(t.dataset_ids || [])[0];
      });
    }
    // Require both a prompt and a dataset before running the preview query, so a
    // missing dataset is caught up front rather than returning an empty chart
    // (PRD-1273 §5).
    if (stepKey === 'configure') {
      if (!prompt.trim() || !selectedDatasets.length) return false;
      // PRD-1336 R5 (Cody #2): a custom range must have both dates and from <= to.
      // An empty custom range silently means all-time, and a reversed range is a
      // silent wrong answer — block Next rather than send "custom:" or a bad window.
      if (timeRange.startsWith('custom:')) {
        if (!customFrom || !customTo) return false;
        if (customFrom > customTo) return false;
      }
      return true;
    }
    // Preview only moves on once something rendered: after a refusal, Next would
    // land on Name and add with Add disabled and the explanation scrolled away.
    if (stepKey === 'preview') return previewRendered;
    return true;
  };

  // The same rule as the card: a result the widget's own setup rules out — not one
  // value, wrong shape, too few points — comes back the same on a re-run. The
  // remedy is a different type or prompt, so the preview offers that instead of Retry.
  const previewNotRetryable = ['not_single_value', 'validation_failed', 'type_mismatch', 'series_not_drawable'].includes(errorType);
  // The datasets themselves can't answer the question together, so the remedy is
  // a different selection, not a re-run or a different chart type.
  const previewNeedsOtherDatasets = errorType === 'datasets_not_combinable';
  const isLastStep = stepKey === 'name';
  const Renderer = queryTypeId ? getRenderer(queryTypeId) : null;

  if (!open) return null;

  // PRD-1448: render through a portal to document.body so the fixed overlay centres
  // over the viewport regardless of any transformed/positioned ancestor (the Insights
  // dashboard and grid establish containing blocks that otherwise shift it off-centre).
  return createPortal(
    <div className="wcm-overlay">
      <div className="wcm-modal">
        {/* Header */}
        <div className="wcm-header">
          <h2 className="wcm-title">{isEditMode ? 'Edit widget' : 'Add widget'}</h2>
          <button className="wcm-close" onClick={handleClose}>
            <Icon name="close" />
          </button>
        </div>
        <div className="wcm-divider" />

        {/* Stepper, once a type is chosen and the flow's length is known. */}
        {selectedWidget && (
          <div className="wcm-stepper">
            {steps.map((label, idx) => (
              <React.Fragment key={label}>
                <div className="wcm-step">
                  <span className={'wcm-step-num' + (idx === step ? ' wcm-step-active' : '') + (idx < step ? ' wcm-step-done' : '')}>
                    {idx < step ? <Icon name="check" style={{ fontSize: '16px' }} /> : idx + 1}
                  </span>
                  <span className={'wcm-step-label' + (idx === step ? ' wcm-step-label-active' : '')}>{label}</span>
                </div>
                {idx < steps.length - 1 && <div className="wcm-step-line" />}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Body */}
        <div className="wcm-body">

          {/* Choose widget type */}
          {stepKey === 'type' && (
            <>
              <h3 className="wcm-heading">What Do You Want To Show?</h3>
              {visibleCategories.map((cat) => (
                <div key={cat.label} className="wcm-category">
                  <p className="wcm-category-label">{cat.label}</p>
                  <div className="wcm-type-grid">
                    {cat.items.map((item) => (
                      <button
                        key={item.id}
                        className={'wcm-type-card' + (selectedWidget === item.id ? ' wcm-type-selected' : '')}
                        onClick={() => { setSelectedWidget(item.id); setSelectedChart(null); }}
                      >
                        <Icon name={item.icon} className="wcm-type-icon" size={28} />
                        <span className="wcm-type-name">{item.name}</span>
                        <span className="wcm-type-desc">{item.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Summary row only: the tiles the strip is made of */}
          {stepKey === 'tiles' && (
            <>
              <h3 className="wcm-heading">Build Your Summary Row</h3>
              <p className="wcm-hint" style={{ marginBottom: '12px' }}>
                Between {MIN_TILES} and {MAX_TILES} tiles. Each one is answered on its own, so a tile
                that fails leaves the rest of the row standing.
              </p>

              {tiles.map((tile, i) => {
                const setTile = (patch) => setTiles(tiles.map((t, j) => (j === i ? { ...t, ...patch } : t)));
                return (
                  <div className="wcm-tile-row" key={i}>
                    <div className="wcm-tile-head">
                      <span className="wcm-tile-index">Tile {i + 1}</span>
                      <button
                        type="button"
                        className="wcm-tile-remove"
                        onClick={() => setTiles(tiles.filter((_, j) => j !== i))}
                        disabled={tiles.length <= MIN_TILES}
                        title={tiles.length <= MIN_TILES ? `A summary row needs at least ${MIN_TILES} tiles` : 'Remove this tile'}
                        aria-label={`Remove tile ${i + 1}`}
                      >
                        <Icon name="close" />
                      </button>
                    </div>

                    <div className="wcm-field">
                      <label className="wcm-label">Kind</label>
                      <select
                        className="resui-select"
                        aria-label={`Tile ${i + 1} kind`}
                        value={tile.kind}
                        onChange={(e) => setTile(
                          e.target.value === 'platform'
                            ? { kind: 'platform', metric: 'datasets', label: tile.label || 'Datasets', prompt: undefined, dataset_ids: undefined }
                            : { kind: 'prompt', metric: undefined, prompt: tile.prompt || '', dataset_ids: tile.dataset_ids || [], time_range: tile.time_range || 'all' },
                        )}
                      >
                        <option value="platform">A count about the Console</option>
                        <option value="prompt">A question about your data</option>
                      </select>
                    </div>

                    {tile.kind === 'platform' ? (
                      <div className="wcm-field">
                        <label className="wcm-label">Metric</label>
                        <select
                          className="resui-select"
                          aria-label={`Tile ${i + 1} metric`}
                          value={tile.metric || ''}
                          onChange={(e) => {
                            const m = TILE_METRICS.find((x) => x.id === e.target.value);
                            // Retitle only while the author has not written their own
                            // label, so a chosen name is never silently replaced.
                            const named = TILE_METRICS.some((x) => x.name === tile.label);
                            setTile({ metric: e.target.value, label: named || !tile.label ? m?.name || '' : tile.label });
                          }}
                        >
                          {TILE_METRICS.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                        </select>
                      </div>
                    ) : (
                      <>
                        <div className="wcm-field">
                          <label className="wcm-label">Dataset</label>
                          <select
                            className="resui-select"
                            aria-label={`Tile ${i + 1} dataset`}
                            value={(tile.dataset_ids || [])[0] || ''}
                            onChange={(e) => setTile({ dataset_ids: e.target.value ? [e.target.value] : [] })}
                          >
                            <option value="">Select a dataset</option>
                            {datasets.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                          </select>
                        </div>
                        <div className="wcm-field">
                          <label className="wcm-label">Question</label>
                          <input
                            className="resui-input"
                            type="text"
                            aria-label={`Tile ${i + 1} question`}
                            placeholder="e.g. How many shifts are unfilled?"
                            value={tile.prompt || ''}
                            onChange={(e) => setTile({ prompt: e.target.value })}
                          />
                          <p className="wcm-hint">Answered as a single number, the same as a Single KPI.</p>
                        </div>
                        <div className="wcm-field">
                          <label className="wcm-label">Time range</label>
                          <select
                            className="resui-select"
                            aria-label={`Tile ${i + 1} time range`}
                            value={tile.time_range || 'all'}
                            onChange={(e) => setTile({ time_range: e.target.value })}
                          >
                            {TILE_RANGES.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                          </select>
                        </div>
                      </>
                    )}

                    <div className="wcm-field">
                      <label className="wcm-label">Label</label>
                      <input
                        className="resui-input"
                        type="text"
                        aria-label={`Tile ${i + 1} label`}
                        placeholder="What this number is"
                        value={tile.label || ''}
                        onChange={(e) => setTile({ label: e.target.value })}
                      />
                    </div>
                  </div>
                );
              })}

              <button
                type="button"
                className="wcm-tile-add"
                onClick={() => setTiles([...tiles, newTile('datasets')])}
                disabled={tiles.length >= MAX_TILES}
              >
                <Icon name="add" />
                Add a tile
              </button>
              {tiles.length >= MAX_TILES && (
                <p className="wcm-hint">{MAX_TILES} is the most a row can hold and still be read at a glance.</p>
              )}
            </>
          )}

          {/* Chart only: choose chart sub-type */}
          {stepKey === 'chart_type' && (
            <>
              <h3 className="wcm-heading">Choose Chart Type</h3>
              {CHART_SUBTYPES.map((group) => (
                <div key={group.label} className="wcm-category">
                  <p className="wcm-category-label">{group.label}</p>
                  <div className="wcm-chart-grid">
                    {group.items.map((item, i) => (
                      <button
                        key={item.id + '-' + i}
                        className={'wcm-chart-card' + (selectedChart === item.id ? ' wcm-chart-selected' : '')}
                        onClick={() => setSelectedChart(item.id)}
                      >
                        <Icon name={item.icon} className="wcm-chart-icon" size={32} />
                        <span className="wcm-chart-name">{item.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}

          {/* Configure */}
          {stepKey === 'configure' && (
            <>
              {/* PRD-1336 R2: single heading (was duplicated as subtitle + h3). */}
              <h3 className="wcm-heading">Configure data and layout</h3>

              {/* PRD-1336 R2: data controls grouped; Dataset sits above Prompt. */}
              <div className="wcm-field wcm-field--full">
                <span className="wcm-label">Datasets</span>
                <DatasetMultiSelect
                  datasets={datasets}
                  selectedIds={selectedDatasets}
                  onChange={setSelectedDatasets}
                />
              </div>

              <label className="wcm-label">Prompt</label>
              <textarea
                className="resui-textarea wcm-prompt-spacing"
                value={prompt}
                onChange={(e) => setPrompt(e.target.value)}
                rows={3}
                autoFocus
              />
              {/* PRD-1336 R2 / D1: persistent guidance instead of a placeholder
                  (a placeholder vanishes the moment the user types).
                  PRD-1370 #7: dropped "or pick an example below" and the example
                  chips — the generated suggestions could produce poor queries
                  (e.g. grouping by a unique key). */}
              <p className="wcm-helper">
                {selectedDatasets.length
                  ? `Describe the ${promptNoun} you want in plain language.`
                  : `Select one or more datasets first, then describe the ${promptNoun} you want in plain language.`}
              </p>

              {/* PRD-1336 R1: sizing is set on the canvas (drag-resize), not here.
                  The Width / Widget Size controls were removed. */}
              {/* PRD-1336 R5: direction-aware time range. No date-direction metadata
                  exists to auto-pick a group, so the Past group leads and Upcoming /
                  Current are one click away. (Custom absolute from-to is the next slice.) */}
              <div className="wcm-field wcm-field--full">
                <label className="wcm-label">Time Range</label>
                <select
                  className="resui-select"
                  value={timeRange.startsWith('custom:') ? 'custom' : timeRange}
                  onChange={(e) => {
                    const v = e.target.value;
                    setTimeRange(v === 'custom' ? (customFrom && customTo ? `custom:${customFrom}:${customTo}` : 'custom:') : v);
                  }}
                >
                  <option value="all">All time</option>
                  <optgroup label="Past">
                    <option value="7d">Last 7 days</option>
                    <option value="30d">Last 30 days</option>
                    <option value="90d">Last 90 days</option>
                    <option value="12m">Last 12 months</option>
                  </optgroup>
                  <optgroup label="Upcoming">
                    <option value="next_7d">Next 7 days</option>
                    <option value="next_30d">Next 30 days</option>
                    <option value="next_90d">Next 90 days</option>
                    <option value="next_12m">Next 12 months</option>
                  </optgroup>
                  <optgroup label="Current">
                    <option value="this_week">This week</option>
                    <option value="this_month">This month</option>
                    <option value="this_quarter">This quarter</option>
                  </optgroup>
                  <option value="custom">Custom range…</option>
                </select>
                {timeRange.startsWith('custom:') && (
                  <div className="wcm-custom-range">
                    <input
                      type="date"
                      className="resui-input"
                      aria-label="From date"
                      value={customFrom}
                      onChange={(e) => { setCustomFrom(e.target.value); setTimeRange(`custom:${e.target.value}:${customTo}`); }}
                    />
                    <span className="wcm-custom-range-sep">to</span>
                    <input
                      type="date"
                      className="resui-input"
                      aria-label="To date"
                      value={customTo}
                      onChange={(e) => { setCustomTo(e.target.value); setTimeRange(`custom:${customFrom}:${e.target.value}`); }}
                    />
                  </div>
                )}
                {/* PRD-1336 R5 (Cody #2): make the custom range unambiguous — surface
                    the resolved window, or why Next is blocked. */}
                {timeRange.startsWith('custom:') && (
                  <p className="wcm-refresh-note">
                    {(!customFrom || !customTo)
                      ? 'Enter both a from and a to date.'
                      : customFrom > customTo
                        ? 'The from date must be on or before the to date.'
                        : `Showing ${customFrom} to ${customTo}.`}
                  </p>
                )}
              </div>

              {/* PRD-1336 R3: how fresh the result must be. Between refreshes the
                  widget serves its last computed result (no query on every load). */}
              <div className="wcm-field wcm-field--full">
                <label className="wcm-label">Refresh</label>
                <select className="resui-select" value={refreshPolicy} onChange={(e) => setRefreshPolicy(e.target.value)}>
                  <option value="manual">Manual only</option>
                  <option value="hourly">Hourly</option>
                  <option value="daily">Daily</option>
                  <option value="custom">Custom</option>
                </select>
                {refreshPolicy === 'daily' && (
                  <div className="wcm-refresh-detail">
                    <input
                      type="time"
                      className="resui-input"
                      aria-label="Daily refresh time"
                      value={refreshTime}
                      onChange={(e) => setRefreshTime(e.target.value)}
                    />
                  </div>
                )}
                {refreshPolicy === 'custom' && (
                  <div className="wcm-refresh-detail">
                    <span className="wcm-refresh-label">every</span>
                    <select className="resui-select" value={refreshInterval} onChange={(e) => setRefreshInterval(e.target.value)}>
                      <option value={5}>5 minutes</option>
                      <option value={15}>15 minutes</option>
                      <option value={30}>30 minutes</option>
                      <option value={60}>60 minutes</option>
                    </select>
                  </div>
                )}
                <p className="wcm-refresh-note">
                  {refreshPolicy === 'manual' && 'Only refreshes when you use the refresh control on the widget.'}
                  {refreshPolicy === 'hourly' && 'Recomputes at most once an hour; serves the cached result in between.'}
                  {refreshPolicy === 'daily' && 'Recomputes once a day at the chosen time; serves the cached result in between.'}
                  {refreshPolicy === 'custom' && 'Recomputes on your interval; serves the cached result in between.'}
                </p>
              </div>

              {/* PRD-1336 R7 (Option A): share this widget with the whole org. Read
                  access only — other members can view it but not edit or delete. */}
              {widgetSharingEnabled && (
                <div className="wcm-field wcm-field--full">
                  <label className="wcm-label">Sharing</label>
                  <label className="resui-switch">
                    <input
                      type="checkbox"
                      className="resui-switch-input"
                      checked={visibility === 'organization'}
                      onChange={(e) => setVisibility(e.target.checked ? 'organization' : 'private')}
                    />
                    <span className="resui-switch-track"><span className="resui-switch-thumb" /></span>
                    <span className="resui-switch-label">
                      {visibility === 'organization' ? 'Shared with organization' : 'Private to me'}
                    </span>
                  </label>
                  <p className="wcm-refresh-note">
                    {visibility === 'organization'
                      ? 'Everyone in your organization can view this widget. Only you can edit or delete it.'
                      : 'Only you can see this widget.'}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Preview */}
          {stepKey === 'preview' && (
            <>
              <h3 className="wcm-heading">Preview</h3>

              {/* Type — read-only display; change via the type step */}
              {!isSummary && previewRendered && (
                <div className="wcm-preview-controls">
                  <span className="wcm-preview-chart-type">
                    {displayNameFor(queryTypeId)}
                  </span>
                  <button
                    type="button"
                    className="wcm-change-type-btn"
                    onClick={() => setStep(onlyChartOffered && isChart ? flow.indexOf('chart_type') : 0)}
                  >
                    Change type
                  </button>
                </div>
              )}

              {isSummary ? (
                <>
                  {/* The same component the card renders, so what is approved here is
                      literally what will be drawn — and the tiles run for real, which
                      is what makes Add mean "this worked" rather than "this parsed". */}
                  <div className="wcm-preview-chart wcm-preview-strip" ref={previewRef}>
                    <SummaryRow
                      widget={{ tiles, title: title || 'Summary row' }}
                      width={previewSize.width || 600}
                      refreshKey={0}
                      onRefreshed={setSummaryResult}
                    />
                  </div>
                  {!summaryResult && <p className="wcm-hint">Running each tile…</p>}
                </>
              ) : null}

              {!isSummary && status === 'loading' && (
                <div className="wcm-preview-state">
                  <Icon name="hourglass_empty" className="wcm-spin" />
                  <p>Querying data...</p>
                </div>
              )}
              {!isSummary && status === 'error' && (
                <div className="wcm-preview-state">
                  <Icon name="error_outline" style={{ fontSize: '36px', color: '#E57373' }} />
                  <p>{error}</p>
                  {previewNeedsOtherDatasets ? (
                    <button type="button" className="wcm-retry-btn" onClick={() => setStep(flow.indexOf('configure'))}>
                      Change datasets
                    </button>
                  ) : previewNotRetryable ? (
                    <button
                      type="button"
                      className="wcm-retry-btn"
                      onClick={() => setStep(onlyChartOffered && isChart ? flow.indexOf('chart_type') : 0)}
                    >
                      Change type
                    </button>
                  ) : (
                    <button className="wcm-retry-btn" onClick={runPreviewQuery}>Retry</button>
                  )}
                </div>
              )}
              {!isSummary && previewRendered && Renderer && (
                <>
                  <div className="wcm-preview-chart" ref={previewRef}>
                    <Renderer data={rendererInput(queryTypeId, data)} width={previewSize.width || 600} height={previewSize.height || 300} xLabel={data?.x_label} yLabel={data?.y_label} title={title || prompt} meta={metadata} displayLabel={isKpi && labelTouched ? label : undefined} labelMode={isKpi} />
                  </div>
                  {/* Points are a chart's; a table states its row count in its own caption. */}
                  {metadata?.truncated && metadata.shown_points != null && (
                    <p className="wcm-preview-truncation">
                      Showing {metadata.shown_points} of {metadata.total_points} points
                    </p>
                  )}
                  {/* PRD-1336 R5: state the absolute range the widget actually used. */}
                  {metadata?.resolved_time_range?.from && metadata?.resolved_time_range?.to && (
                    <p className="wcm-preview-range">Range: {rangeLabel(null, metadata.resolved_time_range)}</p>
                  )}
                  {selectedDatasets.length > 1 && (
                    <p className="wcm-preview-range">Datasets: {selectedDatasetNames.join(', ')}</p>
                  )}
                  {data?.combination && <p className="wcm-preview-range">{data.combination}</p>}
                </>
              )}
            </>
          )}

          {/* Name and add */}
          {stepKey === 'name' && (
            <>
              <p className="wcm-subtitle">Name and add</p>
              <h3 className="wcm-heading">{isKpi ? 'Label Your Value' : 'Name Your Widget'}</h3>

              {isKpi ? (
                <div className="wcm-field wcm-field--full">
                  <label className="wcm-label" htmlFor="wcm-kpi-label">Label</label>
                  <input
                    id="wcm-kpi-label"
                    className="resui-input"
                    type="text"
                    maxLength={200}
                    placeholder="e.g. shifts scheduled this week"
                    value={label}
                    onChange={(e) => { setLabel(e.target.value); setLabelTouched(true); }}
                    autoFocus
                  />
                </div>
              ) : (
                <div className="wcm-field wcm-field--full">
                  <label className="wcm-label">Widget Title</label>
                  <input
                    className="resui-input"
                    type="text"
                    placeholder="Enter widget title"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    autoFocus
                  />
                </div>
              )}

              <p className="wcm-hint">
                {isKpi ? 'Shown under the value on the card. ' : ''}
                {isEditMode ? 'Click "Save" to update the widget.' : 'Click "Add to dashboard" to save.'}
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="wcm-divider" />
        <div className="wcm-footer">
          {/* When only Chart is offered, step 0 is auto-skipped and step 1
            * becomes the effective first step — so show CANCEL (which closes
            * the modal) instead of a BACK that would just loop back to the
            * auto-skipped step. */}
          {onlyChartOffered && stepKey === 'chart_type' ? (
            <button className="wcm-back-btn" onClick={handleClose}>Cancel</button>
          ) : step > 0 ? (
            <button className="wcm-back-btn" onClick={handleBack}>Back</button>
          ) : <div />}

          {isLastStep ? (
            <button
              className="wcm-next-btn"
              onClick={handleSave}
              // A widget that never rendered in the preview cannot be added — for
              // every type, not only charts.
              disabled={!previewRendered}
            >
              {isEditMode ? 'Save' : 'Add to Dashboard'}
            </button>
          ) : (
            <button className="wcm-next-btn" onClick={handleNext} disabled={!canNext()}>Next</button>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default WidgetCreatorModal;
